import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Calendar,
  MessageSquare,
  Paperclip,
  Edit2,
  Trash2,
  MoreVertical,
  UserPlus,
  Tag,
  CheckSquare,
  Plus,
  X,
  Archive,
  RotateCcw,
  Check,
  Square,
  Clock4,
  Bookmark,
} from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { useNotification } from "../contexts/NotificationContext";
import { useBookmark } from "../contexts/BookmarkContext";
import { cardItemAPI, cardAPI } from "../utils/api";
import CardModal from "./CardModal";
import Avatar from "./Avatar";
import ConfirmationModal from "./ConfirmationModal";
import SafeImage from "./SafeImage";
import {
  getCardStatusColors,
  getPriorityColors,
  getStatusBadgeClasses,
} from "../utils/statusColors";
import { formatDuration, formatTimer, formatRelativeTime } from "./TimeTracking/timeUtils";
import { features } from "../config/features";
import { useCardImageVisibility } from "../contexts/CardImageVisibilityContext";

// Label color palette - shared between the visible label badge and the
// hover tooltip that lists every label on the card.
const LABEL_COLORS = [
  { value: "light-green", bg: "bg-green-300", text: "text-black" },
  { value: "green", bg: "bg-green-500", text: "text-white" },
  { value: "dark-green", bg: "bg-green-700", text: "text-white" },
  { value: "light-yellow", bg: "bg-yellow-300", text: "text-black" },
  { value: "yellow", bg: "bg-yellow-500", text: "text-black" },
  { value: "dark-yellow", bg: "bg-yellow-700", text: "text-white" },
  { value: "orange", bg: "bg-orange-500", text: "text-white" },
  { value: "red", bg: "bg-red-500", text: "text-white" },
  { value: "purple", bg: "bg-purple-500", text: "text-white" },
  { value: "pink", bg: "bg-pink-500", text: "text-white" },
  { value: "blue", bg: "bg-blue-500", text: "text-white" },
  { value: "gray", bg: "bg-gray-500", text: "text-white" },
];

// Light label colors are mapped to their saturated equivalents for consistency.
const LABEL_COLOR_ALIASES = {
  "light-green": "green",
  "light-yellow": "yellow",
};

const getLabelColorConfig = (label) => {
  const colorKey = LABEL_COLOR_ALIASES[label.color] || label.color;
  return (
    LABEL_COLORS.find((c) => c.value === colorKey) ||
    LABEL_COLORS.find((c) => c.value === "blue")
  );
};

const LabelBadge = ({ label, className = "" }) => {
  const { bg, text } = getLabelColorConfig(label);
  return (
    <span
      title={label.name}
      className={`inline-flex items-center h-6 px-2.5 rounded-lg text-xs font-medium whitespace-nowrap ${bg} ${text} ${className}`}
    >
      {label.name}
    </span>
  );
};

// Memoized card cover image. SafeImage skips (renders nothing) when the file is
// missing/404s instead of looping on a broken fallback. Memoized to avoid
// blinking on unrelated re-renders (e.g. sibling cards displaced during a drag).
const CardImage = React.memo(
  ({ imageUrl, altText }) => (
    <div className="mb-3 relative cursor-pointer">
      <SafeImage
        src={imageUrl}
        alt={altText}
        className="w-full h-24 object-cover rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800"
        loading="lazy"
        fallback="skip"
      />
    </div>
  ),
  (prevProps, nextProps) => prevProps.imageUrl === nextProps.imageUrl
);

const CardItem = React.memo(
  ({
    card,
    onCardUpdated,
    onCardDeleted,
    onCardRestored,
    onStatusChange,
    onCardClick,
    activeTimer,
    activeTimers,
    onTimerStart,
    onTimerStop,
    projectId,
  }) => {
    // Normalize card id for comparisons
    const cardId = card._id || card.id;
    const { user, users } = useUser();
    const { showToast } = useNotification();
    const { isBookmarked, toggleBookmark } = useBookmark();
    const bookmarked = isBookmarked(card._id);
    const { showCardImages } = useCardImageVisibility();
    const [liveElapsed, setLiveElapsed] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editTitle, setEditTitle] = useState(card.title);
    const [items, setItems] = useState([]);
    const dropdownRef = useRef(null);
    const titleInputRef = useRef(null);

    // Memoize first image data to prevent recalculation and image blinking
    const firstImageData = useMemo(() => {
      const firstImage = card.attachments?.find((attachment) => {
        // Check by MIME type first
        if (attachment.mimeType?.startsWith("image/")) {
          return true;
        }
        // Check by file extension
        if (
          attachment.url?.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff|ico)$/i)
        ) {
          return true;
        }
        // Check by filename
        if (
          attachment.originalName?.match(
            /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff|ico)$/i
          )
        ) {
          return true;
        }
        return false;
      });

      if (!firstImage) return null;

      // Pass the raw stored URL; SafeImage resolves it to a full URL.
      return {
        url: firstImage.url,
        altText: firstImage.originalName || "Card attachment",
        id: firstImage._id || firstImage.url,
      };
    }, [card.attachments]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setShowActions(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    // Focus input when editing starts
    useEffect(() => {
      if (isEditingTitle && titleInputRef.current) {
        titleInputRef.current.focus();
        titleInputRef.current.select();
      }
    }, [isEditingTitle]);

    const fetchItems = useCallback(async () => {
      try {
        const response = await cardItemAPI.getCardItems(card._id);
        setItems(response.data.items || []);
      } catch (error) {
        console.error("Error fetching card items:", error);
      }
    }, []);

    // Fetch card items
    // useEffect(() => {
    //   fetchItems();
    // }, []);

    const handleToggleItem = async (itemId, completed) => {
      try {
        const response = await cardItemAPI.updateCardItem(card._id, itemId, {
          completed: !completed,
        });

        if (response.data.success) {
          setItems((prev) =>
            prev.map((item) =>
              item._id === itemId ? { ...item, completed: !completed } : item
            )
          );
        }
      } catch (error) {
        console.error("Error updating item:", error);
        showToast("Failed to update item", "error");
      }
    };

    const handleDeleteItem = async (itemId) => {
      try {
        const response = await cardItemAPI.deleteCardItem(card._id, itemId);

        if (response.data.success) {
          setItems((prev) => prev.filter((item) => item._id !== itemId));
          showToast("Item deleted successfully!", "success");
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        showToast("Failed to delete item", "error");
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = date - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return { text: "Overdue", color: "text-red-600 bg-red-100" };
      } else if (diffDays === 0) {
        return { text: "Due today", color: "text-orange-600 bg-orange-100" };
      } else if (diffDays === 1) {
        return { text: "Due tomorrow", color: "text-yellow-600 bg-yellow-100" };
      } else {
        return {
          text: `Due in ${diffDays} days`,
          color: "text-blue-600 bg-blue-100",
        };
      }
    };

    const getAssignees = () => {
      // If assignees are already populated objects, return them directly
      if (
        card.assignees &&
        card.assignees.length > 0 &&
        typeof card.assignees[0] === "object"
      ) {
        return card.assignees;
      }
      // Otherwise, map from user IDs
      return card.assignees
        .map((userId) => users.find((user) => user._id === userId))
        .filter(Boolean);
    };

    const confirmQuickDelete = async () => {
      try {
        const { cardAPI } = await import("../utils/api");
        await cardAPI.archiveCard(card._id);
        onCardDeleted(card._id);
        showToast("Card archived successfully!", "success");
      } catch (error) {
        showToast("Failed to archive card", "error");
      } finally {
        setShowDeleteConfirm(false);
      }
    };

    const handleRestore = async () => {
      try {
        // const { cardAPI } = await import("../utils/api");
        // await cardAPI.restoreCard(card._id);
        onCardRestored(card._id);
        // showToast("Card restored successfully!", "success");
      } catch (error) {
        showToast("Failed to restore card", "error");
      }
    };

    const handleCompleteToggle = async (e) => {
      e.stopPropagation();
      try {
        const response = await cardAPI.toggleComplete(card._id);
        onCardUpdated(response.data.card);
        showToast(
          response.data.card.isComplete
            ? "Card marked as complete!"
            : "Card marked as incomplete!",
          "success"
        );
      } catch (error) {
        console.error("Error toggling completion:", error);
        showToast("Failed to toggle completion", "error");
      }
    };

    const handleQuickMove = (e, newStatus) => {
      e.stopPropagation();
      onStatusChange(card._id, newStatus);
      const statusLabels = {
        todo: "To Do",
        doing: "Doing",
        review: "Review",
        done: "Done",
      };
      showToast(`Card moved to ${statusLabels[newStatus]}`, "success");
    };

    const handleQuickAssign = (e) => {
      e.stopPropagation();
      if (onCardClick) {
        onCardClick(card);
      } else {
        setShowModal(true);
      }
    };

    const handleTitleEdit = (e) => {
      e.stopPropagation();
      setIsEditingTitle(true);
      setEditTitle(card.title);
    };

    const handleTitleSave = async () => {
      if (editTitle.trim() && editTitle !== card.title) {
        try {
          const { cardAPI } = await import("../utils/api");
          const response = await cardAPI.updateCard(card._id, {
            ...card,
            title: editTitle.trim(),
          });

          onCardUpdated(response.data.card);
          showToast("Card title updated!", "success");
        } catch (error) {
          console.error("Error updating title:", error);
          showToast("Failed to update title", "error");
        }
      }
      setIsEditingTitle(false);
    };

    const handleTitleKeyPress = (e) => {
      if (e.key === "Enter") {
        handleTitleSave();
      } else if (e.key === "Escape") {
        setEditTitle(card.title);
        setIsEditingTitle(false);
      }
    };

    const dueDateInfo = formatDate(card.dueDate);
    const assignees = getAssignees();
    // The project-wide map of running timers (keyed by cardId) carries who
    // started each one - `activeTimer` alone only ever reflects the current
    // viewer's own timer, so other users' running timers wouldn't show at all.
    const projectTimerOnCard = activeTimers?.[cardId];
    const isOwnTimerRunningOnCard =
      activeTimer?.isRunning &&
      String(activeTimer?.cardId) === String(cardId);
    const isTimerRunningOnCard =
      isOwnTimerRunningOnCard || Boolean(projectTimerOnCard);
    const timerStartedBy = projectTimerOnCard
      ? {
          name: projectTimerOnCard.userName,
          avatar: projectTimerOnCard.userAvatar,
          color: projectTimerOnCard.userColor,
        }
      : isOwnTimerRunningOnCard
      ? user
      : null;
    const totalTimeSeconds = (card.totalTimeSpent || 0) + liveElapsed;
    const formattedTime =
      totalTimeSeconds > 0
        ? formatDuration(totalTimeSeconds)
        : isTimerRunningOnCard
        ? "0s"
        : "-";

    // Keep a local ticking elapsed counter so the card updates every second.
    // Prefer the viewer's own timer state, but fall back to the project-wide
    // timer data so another user's running timer still ticks live here.
    const effectiveStartedAt = isOwnTimerRunningOnCard
      ? activeTimer?.startedAt
      : projectTimerOnCard?.startedAt;
    const effectiveAccumulatedSeconds = isOwnTimerRunningOnCard
      ? activeTimer?.accumulatedSeconds
      : projectTimerOnCard?.accumulatedSeconds;

    useEffect(() => {
      if (!isTimerRunningOnCard || !effectiveStartedAt) {
        setLiveElapsed(0);
        return;
      }

      const calcElapsed = () => {
        // Use accumulatedSeconds (from pauses) + elapsed time since timer started
        const accumulated =
          typeof effectiveAccumulatedSeconds === "number"
            ? effectiveAccumulatedSeconds
            : 0;
        const runSeconds = Math.floor(
          (Date.now() - new Date(effectiveStartedAt).getTime()) / 1000
        );
        return Math.max(accumulated + runSeconds, 0);
      };

      setLiveElapsed(calcElapsed());

      const intervalId = setInterval(() => {
        setLiveElapsed(calcElapsed());
      }, 1000);

      return () => clearInterval(intervalId);
    }, [isTimerRunningOnCard, effectiveStartedAt, effectiveAccumulatedSeconds]);

    // Check if card is unread by current user
    const isUnread = React.useMemo(() => {
      if (!user || !card.readBy || !Array.isArray(card.readBy)) {
        return true; // Default to unread if no readBy data
      }
      return !card.readBy.some(
        (readEntry) =>
          (readEntry.user?._id || readEntry.user) === (user._id || user.id)
      );
    }, [card.readBy, user]);

    // Make card draggable
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: card._id || card.id,
      data: {
        type: "card",
        status: card.status,
      },
      disabled: card.isArchived, // Disable dragging for archived cards
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      // No transition on the actively dragged item so it tracks the pointer
      // 1:1; displaced siblings keep dnd-kit's transition for a smooth slide.
      transition: isDragging ? "none" : transition,
      opacity: isDragging ? 0.5 : 1,
    };

    // Handle card click - prevent if dragging
    const handleCardClick = (e) => {
      // Don't open modal if we're dragging or if click is on an interactive element
      if (isDragging || e.target.closest("button, a, input")) {
        return;
      }
      if (onCardClick) {
        onCardClick(card);
      } else {
        setShowModal(true);
      }
    };

    return (
      <>
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...listeners}
          className={`bg-white dark:bg-slate-800 rounded-lg shadow-sm border transition-shadow duration-200 group relative cursor-grab active:cursor-grabbing ${
            isUnread
              ? "border-l-blue-500 border-l hover:shadow-md hover:border-l-blue-600 dark:border-l-blue-400"
              : "border-gray-200 dark:border-slate-700 hover:shadow-md hover:border-gray-300 dark:hover:border-slate-600"
          } ${isDragging ? "shadow-xl z-50 opacity-50" : ""}`}
          onClick={handleCardClick}
        >
          {/* Unread Indicator Dot */}
          {/* {isUnread && (
          <div className="absolute top-2 right-2 w-2.5 h-2.5 bg-blue-500 rounded-full z-10" />
        )} */}
          {/* Card Header with Issue Number */}
            <div className="p-3 pb-0 relative z-10">
              <div className="flex items-start justify-between gap-1.5 mb-2">
                <div className="flex-1 min-w-0 relative overflow-hidden">
                  {/* Completion Checkbox - Show on hover or when completed, but hidden when archived */}
                  {!card.isArchived && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCompleteToggle(e);
                      }}
                      className={`absolute left-0 top-0 z-20 w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-all duration-200 ease-in-out ${
                        card.isComplete
                          ? "opacity-100"
                          : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
                      }`}
                      title={
                        card.isComplete
                          ? "Mark as incomplete"
                          : "Mark as complete"
                      }
                    >
                      {card.isComplete ? (
                        <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center text-white hover:bg-green-600 transition-colors duration-200">
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </div>
                      ) : (
                        <div className="w-4 h-4 border-2 border-gray-400 dark:border-slate-500 rounded-full hover:border-gray-600 dark:hover:border-slate-300 transition-colors duration-200"></div>
                      )}
                    </button>
                  )}

                  <div className="min-w-0">
                    {/* Only the title shifts to make room for the completion
                        checkbox on hover - the metadata row below stays put. */}
                    <div
                      className={`transition-transform duration-200 ease-in-out ${
                        card.isComplete && !card.isArchived
                          ? "translate-x-6"
                          : card.isArchived
                          ? "translate-x-0"
                          : "translate-x-0 group-hover:translate-x-6"
                      }`}
                    >
                      <h4
                        className={`text-sm font-medium text-[#292a2e] dark:text-white leading-5 line-clamp-5 break-words transition-colors duration-200`}
                      >
                        {card.title}
                      </h4>
                    </div>
                  </div>
                </div>

                {/* Bookmark Icon Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleBookmark(card);
                  }}
                  className={`w-5 h-5 flex items-center justify-center rounded-md !cursor-pointer transition-all duration-200 shrink-0 relative z-20 ${
                    bookmarked
                      ? "opacity-100 text-amber-500 hover:text-amber-600"
                      : "opacity-0 group-hover:opacity-100 text-gray-400 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-slate-700"
                  }`}
                  title={bookmarked ? "Remove bookmark" : "Bookmark card"}
                >
                  <Bookmark className={`w-4 h-4 pointer-events-none ${bookmarked ? "fill-amber-500 text-amber-500" : ""}`} />
                </button>
              </div>
              {/* Metadata row: ID > Comments > Attachments > Priority >
                      Time Tracking - always one line; a missing item just
                      isn't rendered, so the rest sit flush with no gap. */}
                  <div className="flex items-center flex-nowrap gap-2 text-xs text-gray-500 dark:text-slate-400 overflow-hidden mb-1">
                    <span className="inline-flex items-center h-6 bg-gray-200 dark:bg-slate-700 text-black dark:text-white font-medium px-2.5 rounded-lg shrink-0 transition-colors duration-200">
                      #{card.cardNumber || card._id?.slice(-4) || "0000"}
                    </span>

                    {(() => {
                      const userCommentsCount = card.commentCount ?? 0;

                      if (userCommentsCount <= 0) return null;

                      return (
                        <span className="relative inline-flex items-center h-6 gap-1 shrink-0">
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span className="font-medium">
                            {userCommentsCount}
                          </span>
                          {card.hasUnseenComments && (
                            <span
                              className="absolute -top-0 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-white dark:border-slate-800"
                              title="New comment"
                            />
                          )}
                        </span>
                      );
                    })()}

                    {card.attachments && card.attachments.length > 0 && (
                      <span className="inline-flex items-center h-6 gap-1 shrink-0">
                        <Paperclip className="w-3.5 h-3.5" />
                        <span className="font-medium">
                          {card.attachments.length}
                        </span>
                      </span>
                    )}

                    {card.priority && (
                      <span
                        className={`${getStatusBadgeClasses(
                          "priority",
                          card.priority
                        )} shrink-0`}
                      >
                        {getPriorityColors(card.priority).label}
                      </span>
                    )}

                    {features.timeTracking &&
                      (totalTimeSeconds > 0 || isTimerRunningOnCard) && (
                        <span className="inline-flex items-center h-6 gap-1 shrink-0">
                          <Clock4 className="w-3.5 h-3.5" />
                          <span
                            className={
                              isTimerRunningOnCard
                                ? "text-green-700 dark:text-green-400"
                                : "text-gray-700 dark:text-slate-300"
                            }
                          >
                            {isTimerRunningOnCard ? (
                              <span className="font-medium text-green-600 dark:text-green-400 animate-pulse transition-colors duration-200">
                                {formatDuration(totalTimeSeconds)}
                              </span>
                            ) : (
                              formattedTime
                            )}
                          </span>
                          {isTimerRunningOnCard && timerStartedBy?.name && (
                            <Avatar
                              user={timerStartedBy}
                              size="xs"
                              showTooltip={true}
                              className="!w-4 !h-4 !text-[8px] ring-1 ring-green-500"
                            />
                          )}
                        </span>
                      )}
                  </div>

              {/* Action Buttons - Show on Hover */}
              {/* <div className="flex items-center space-x-1 transition-opacity duration-200 opacity-0 group-hover:opacity-100">
                <button
                onClick={handleQuickEdit}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                title="Edit card"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
                <button
                onClick={handleQuickDelete}
                className="p-1.5 rounded hover:bg-red-100 text-gray-500 hover:text-red-600 transition-colors duration-200"
                title="Delete card"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(!showActions);
                    }}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                    title="More actions"
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </button>

                  {showActions && (
                    <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                      <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                        Move to
                      </div>
                      <button
                        onClick={(e) => handleQuickMove(e, "todo")}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                      >
                        To Do
                      </button>
                      <button
                        onClick={(e) => handleQuickMove(e, "doing")}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-700"
                      >
                        Doing
                      </button>
                      <button
                        onClick={(e) => handleQuickMove(e, "review")}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700"
                      >
                        Review
                      </button>
                      <button
                        onClick={(e) => handleQuickMove(e, "done")}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700"
                      >
                        Done
                      </button>
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={handleQuickAssign}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                        >
                          <UserPlus className="w-4 h-4" />
                          <span>Assign members</span>
                        </button>
                        {card.isArchived && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestore();
                              setShowActions(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center space-x-2"
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span>Restore card</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div> */}
            </div>

            {/* Card Image or Description - Using memoized CardImage component */}
            {firstImageData && showCardImages ? (
              <CardImage
                imageUrl={firstImageData.url}
                altText={firstImageData.altText}
                imageId={firstImageData.id}
              />
            ) : null}

          {/* Card Footer */}
          <div className="px-3 pb-2">
            {/* Remaining metadata row: Overdue > Label. Card ID, Comments,
                Attachments, Priority, and Time Tracking now live in the row
                under the title. */}
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-slate-400">
              {dueDateInfo && (
                <span
                  className={`inline-flex items-center h-6 gap-1 px-2.5 rounded-lg font-medium shrink-0 ${dueDateInfo.color}`}
                >
                  <Calendar className="w-3.5 h-3.5" />
                  {dueDateInfo.text}
                </span>
              )}

              {card.labels && card.labels.length > 0 && (
                <span className="relative group/labels inline-flex items-center h-6 gap-1 shrink-0 max-w-full">
                  <LabelBadge
                    label={card.labels[0]}
                    className="truncate max-w-[180px]"
                  />
                  {card.labels.length > 1 && (
                    <span className="font-medium cursor-default shrink-0">
                      +{card.labels.length - 1}
                    </span>
                  )}

                  {/* All-labels tooltip on hover */}
                  <span className="invisible group-hover/labels:visible opacity-0 group-hover/labels:opacity-100 transition-opacity duration-150 absolute bottom-full left-0 mb-1.5 z-30 flex flex-wrap gap-1 p-2 max-w-[220px] bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg">
                    {card.labels.map((label) => (
                      <LabelBadge key={label._id || label.id} label={label} />
                    ))}
                  </span>
                </span>
              )}
            </div>

            {/* Bottom row: Creator/Timestamp (left) + Assignee Avatars (right) -
                always the last line, pinned to the card's bottom. */}
            {(card.createdAt || assignees.length > 0) && (
              <div className="mt-1 flex items-center justify-between gap-2">
                {card.createdAt ? (
                  <div className="min-w-0 flex-1 truncate text-[11px] text-gray-400 dark:text-slate-500">
                    {card.createdBy?.name && (
                      <span>By {card.createdBy.name} · </span>
                    )}
                    {formatRelativeTime(card.createdAt)}
                  </div>
                ) : (
                  <span />
                )}

                {assignees.length > 0 && (
                  <div className="flex -space-x-1 shrink-0">
                    {assignees.slice(0, 3).map((user) => (
                      <div
                        key={user.id}
                        className="border-2 border-white dark:border-slate-800 shadow-sm rounded-full transition-colors duration-200"
                      >
                        <Avatar user={user} size="xs" showTooltip={true} />
                      </div>
                    ))}
                    {assignees.length > 3 && (
                      <div className="w-6 h-6 bg-gray-300 dark:bg-slate-700 rounded-full border-2 border-white dark:border-slate-800 flex items-center justify-center text-xs text-gray-600 dark:text-slate-300 font-medium shadow-sm transition-colors duration-200">
                        +{assignees.length - 3}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Card Items Section with Progress */}
          {items.length > 0 && (
            <div className="px-3 pb-2">
              {/* Progress Bar */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">
                  {items.filter((item) => item.completed).length}/{items.length}{" "}
                  items
                </span>
                <div className="w-16 h-1 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{
                      width: `${
                        (items.filter((item) => item.completed).length /
                          items.length) *
                        100
                      }%`,
                    }}
                  />
                </div>
              </div>

              {/* Items List */}
              {/* <div className="space-y-1">
              {items.slice(0, 3).map((item) => (
                <div
                  key={item._id}
                  className="flex items-center space-x-2 text-xs text-gray-600"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleItem(item._id, item.completed);
                    }}
                    className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${
                      item.completed
                        ? "bg-green-500 border-green-500 text-white"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    {item.completed && <CheckSquare className="w-2 h-2" />}
                  </button>
                  <span
                    className={`flex-1 ${
                      item.completed ? "line-through text-gray-400" : ""
                    }`}
                  >
                    {item.title}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteItem(item._id);
                    }}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {items.length > 3 && (
                <p className="text-xs text-gray-400 pl-5">
                  +{items.length - 3} more items
                </p>
              )}
            </div> */}
            </div>
          )}

          {/* Click overlay for opening modal */}
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={async () => {
              // Mark card as read when opened — also re-marks an already-read
              // card so the new-comment dot (hasUnseenComments) clears too,
              // not just the first-ever-open unread state.
              if ((isUnread || card.hasUnseenComments) && user) {
                try {
                  const { cardAPI } = await import("../utils/api");
                  await cardAPI.markAsRead(card._id);
                  // Update card locally to reflect read status
                  if (onCardUpdated) {
                    const alreadyInReadBy = (card.readBy || []).some(
                      (r) => (r.user?._id || r.user) === (user._id || user.id)
                    );
                    const updatedCard = {
                      ...card,
                      readBy: alreadyInReadBy
                        ? card.readBy
                        : [
                            ...(card.readBy || []),
                            { user: user._id || user.id, readAt: new Date() },
                          ],
                      hasUnseenComments: false,
                    };
                    onCardUpdated(updatedCard);
                  }
                } catch (error) {
                  console.error("Error marking card as read:", error);
                  // Continue to open modal even if marking as read fails
                }
              }

              if (onCardClick) {
                onCardClick(card);
              } else {
                setShowModal(true);
              }
            }}
          />
        </div>

        {/* Card Modal */}
        {showModal && (
          <CardModal
            card={card}
            onClose={() => setShowModal(false)}
            onCardUpdated={onCardUpdated}
            onCardDeleted={onCardDeleted}
            onCardRestored={onCardRestored}
            onStatusChange={onStatusChange}
            activeTimer={activeTimer}
            onTimerStart={onTimerStart}
            onTimerStop={onTimerStop}
          />
        )}

        {/* Delete Card Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={confirmQuickDelete}
          title="Delete Card"
          message={`Are you sure you want to delete "${card.title}"? This action cannot be undone.`}
          confirmText="Delete Card"
          cancelText="Cancel"
          type="danger"
          isLoading={false}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders (especially for images)
    if (prevProps.projectId !== nextProps.projectId) {
      return false;
    }

    // Compare card properties that affect rendering
    const prevCard = prevProps.card;
    const nextCard = nextProps.card;

    if (
      prevCard._id !== nextCard._id ||
      prevCard.title !== nextCard.title ||
      prevCard.description !== nextCard.description ||
      prevCard.status !== nextCard.status ||
      prevCard.priority !== nextCard.priority ||
      prevCard.dueDate !== nextCard.dueDate ||
      prevCard.order !== nextCard.order ||
      prevCard.isArchived !== nextCard.isArchived ||
      prevCard.isComplete !== nextCard.isComplete ||
      prevCard.completedAt !== nextCard.completedAt ||
      prevCard.totalTimeSpent !== nextCard.totalTimeSpent ||
      prevCard.estimatedTime !== nextCard.estimatedTime
    ) {
      return false;
    }

    const getIdsString = (arr) =>
      (arr || [])
        .map((item) => String(item?._id || item?.id || item?.name || item || ""))
        .sort()
        .join(",");

    if (getIdsString(prevCard.labels) !== getIdsString(nextCard.labels)) {
      return false;
    }

    if (getIdsString(prevCard.assignees) !== getIdsString(nextCard.assignees)) {
      return false;
    }

    if (prevCard.attachments?.length !== nextCard.attachments?.length) {
      return false;
    }

    // readBy length drives the unread indicator.
    if (prevCard.readBy?.length !== nextCard.readBy?.length) {
      return false;
    }

    const prevCommentCount = prevCard.commentCount ?? 0;
    const nextCommentCount = nextCard.commentCount ?? 0;
    if (prevCommentCount !== nextCommentCount) {
      return false;
    }

    // Drives the new-comment red dot.
    if (prevCard.hasUnseenComments !== nextCard.hasUnseenComments) {
      return false;
    }

    const prevActiveOnCard =
      prevProps.activeTimer?.isRunning &&
      String(prevProps.activeTimer?.cardId) === String(prevCard._id || prevCard.id);
    const nextActiveOnCard =
      nextProps.activeTimer?.isRunning &&
      String(nextProps.activeTimer?.cardId) === String(nextCard._id || nextCard.id);

    if (prevActiveOnCard !== nextActiveOnCard) {
      return false;
    }

    if (
      nextActiveOnCard &&
      prevProps.activeTimer?.elapsedSeconds !==
        nextProps.activeTimer?.elapsedSeconds
    ) {
      return false;
    }

    if (
      nextActiveOnCard &&
      prevProps.activeTimer?.startedAt !== nextProps.activeTimer?.startedAt
    ) {
      return false;
    }

    // Project-wide timer map drives who-started-it display for every viewer,
    // not just the person who started the timer - compare this card's entry.
    const cardKey = String(nextCard._id || nextCard.id);
    const prevProjectTimer = prevProps.activeTimers?.[cardKey];
    const nextProjectTimer = nextProps.activeTimers?.[cardKey];
    if (
      prevProjectTimer?.startedAt !== nextProjectTimer?.startedAt ||
      prevProjectTimer?.accumulatedSeconds !==
        nextProjectTimer?.accumulatedSeconds ||
      prevProjectTimer?.userName !== nextProjectTimer?.userName ||
      Boolean(prevProjectTimer) !== Boolean(nextProjectTimer)
    ) {
      return false;
    }

    // Props are equal, don't re-render
    return true;
  }
);

export default CardItem;
